import { useRef, useState } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { useFieldGrants, useScenery, useUploadScenery, type SceneryKind, type SceneryPhoto } from '@/lib/field';
import { shortDate } from '@/lib/format';
import { shrinkImage } from '@/lib/image';
import { usePhotoUrl } from '@/lib/usePhotoUrl';
import { useSite } from '@/lib/useSite';

/** 웹 전경사진 탭의 키 — 올리기는 등록 권한이다(`SiteSceneryController`). */
const SCENERY_MENU = 'site_pmis_scenery';

const KIND_LABEL: Record<SceneryKind, string> = { SCENERY: '전경사진', CONSTRUCTION: '시공사진' };
const KINDS: SceneryKind[] = ['SCENERY', 'CONSTRUCTION'];

/** 이 주의 월요일(로컬). 웹 업로드 창의 주차 기본값과 같다. */
function thisMonday(): string {
  const now = new Date();
  const offset = (now.getDay() + 6) % 7;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
  const two = (value: number) => String(value).padStart(2, '0');
  return `${monday.getFullYear()}-${two(monday.getMonth() + 1)}-${two(monday.getDate())}`;
}

/**
 * 현장사진 — 웹 「전경사진」 탭과 같은 두 갈래(전경·시공)를 나란히 둔다.
 *
 * <p>여러 장을 한 번에 고르고, 갈래는 <b>누른 쪽</b>이 정한다(웹의 끌어다 놓기와 같은 규칙). 주차는 이 주의
 * 월요일이 기본이고 고칠 수 있다 — 비워 두면 서버가 올린 날에서 낸다.
 */
export function SceneryPage() {
  const { siteId, siteName, readOnly } = useSite();
  const { can } = useFieldGrants();
  const canCreate = can(SCENERY_MENU, 'create') && !readOnly;

  const photos = useScenery(siteId);
  const upload = useUploadScenery(siteId);
  const picker = useRef<HTMLInputElement>(null);
  const [pickKind, setPickKind] = useState<SceneryKind>('SCENERY');
  const [weekOf, setWeekOf] = useState(thisMonday);
  const [progress, setProgress] = useState<{ done: number; total: number; failed: number } | null>(null);

  const rows = photos.data ?? [];

  async function send(files: File[], kind: SceneryKind) {
    setProgress({ done: 0, total: files.length, failed: 0 });
    let failed = 0;
    // 한 장씩 보낸다 — 현장 LTE 에서 열 장을 동시에 밀면 다 같이 늦는다.
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      if (file === undefined) continue;
      try {
        await upload.mutateAsync({ file: await shrinkImage(file), photoKind: kind, weekOf: weekOf === '' ? null : weekOf });
      }
      catch {
        failed += 1;
      }
      setProgress({ done: index + 1, total: files.length, failed });
    }
  }

  const busy = progress !== null && progress.done < progress.total;

  return (
    <>
      <PageHeader title="현장사진" subtitle={siteName} />

      {canCreate && (
        <div className="card mb-3">
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>주차(월요일)</span>
            <input type="date" className="input-field" value={weekOf} onChange={(e) => setWeekOf(e.target.value)} />
          </label>
          <input
            ref={picker}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              if (files.length > 0) void send(files, pickKind);
            }}
          />
          <div className="flex gap-2">
            {KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className="btn-primary flex-1"
                disabled={busy}
                onClick={() => {
                  setPickKind(kind);
                  picker.current?.click();
                }}
              >
                {KIND_LABEL[kind]} 올리기
              </button>
            ))}
          </div>
          {progress !== null && (
            <p className="mt-2 text-ui-xs" style={{ color: progress.failed > 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>
              {progress.done} / {progress.total}
              {progress.failed > 0 && ` · ${progress.failed}장 올리지 못했습니다.`}
            </p>
          )}
        </div>
      )}

      <QueryState pending={photos.isPending} error={photos.isError} empty={rows.length === 0} emptyText="올린 사진이 없습니다.">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {KINDS.map((kind) => {
            // 최근 것부터 열두 장 — 사진마다 서버를 거쳐 받으므로 한 화면에 수십 장을 끌어오지 않는다.
            const mine = rows
              .filter((row) => (row.photoKind === 'CONSTRUCTION' ? 'CONSTRUCTION' : 'SCENERY') === kind)
              .sort((a, b) => (b.weekOf + (b.takenAt ?? '')).localeCompare(a.weekOf + (a.takenAt ?? '')));
            return (
              <section key={kind} className="card">
                <h2 className="mb-2 text-ui-sm font-semibold">
                  {KIND_LABEL[kind]} <span style={{ color: 'var(--text-muted)' }}>{mine.length}</span>
                </h2>
                <div className="grid grid-cols-3 gap-1.5">
                  {mine.slice(0, 12).map((photo) => (
                    <Thumb key={photo.id} siteId={siteId} photo={photo} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </QueryState>
    </>
  );
}

/** 한 장. 저장소 링크는 포털이 열 수 없어(CSP) 서버를 거쳐 받는다. */
function Thumb({ siteId, photo }: { siteId: string; photo: SceneryPhoto }) {
  const url = usePhotoUrl(`/sites/${siteId}/scenery/${photo.id}/file`);
  return (
    <figure className="min-w-0">
      <div className="aspect-square overflow-hidden rounded" style={{ background: 'var(--bg-hover)' }}>
        {url !== null && <img src={url} alt={photo.caption ?? photo.fileName ?? ''} className="h-full w-full object-cover" />}
      </div>
      <figcaption className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
        {shortDate(photo.weekOf)}
      </figcaption>
    </figure>
  );
}
